import { useState, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import { Grid, Text } from '@simplywallst/ui-core';
import ChevronUpSmallSolid from '@simplywallst/ui-core/icons/ChevronUpSmallSolid';
import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';
import loadable from '@loadable/component';
import If from '@/components/If';
import useUser from '@/hooks/useUser';

import useVoteOnNarrative from '@/pages/Narrative/hooks/useVoteOnNarrative';
import useNarrativePermission from '@/pages/Narrative/hooks/useNarrativePermission';

import { StyledToggleButton } from './styled';

const GuestRegistrationModal = loadable(
  () => import('@/components/GuestRegistrationModal')
);

export interface Props {
  narrativeId: string;
  slug: string;
  upVotes: number;
  downVotes: number;
  companyId?: string;
}

const NarrativeVoting = ({
  narrativeId,
  slug,
  upVotes,
  downVotes,
  companyId,
}: Props) => {
  const [showRegoModal, setShowRegoModal] = useState(false);
  const { userVotedNarratives } = useNarrativePermission();
  const { isUpVoted, isDownVoted } = useMemo(() => {
    const existingVote = userVotedNarratives.find(
      (n) => n.narrativeId === narrativeId
    );
    const isUpVoted = existingVote?.voteType === 1;
    const isDownVoted = existingVote?.voteType === -1;
    return { isUpVoted, isDownVoted };
  }, [userVotedNarratives]);
  const { mutate } = useVoteOnNarrative(slug, companyId);
  const {
    data: { loggedIn },
  } = useUser();
  const { trackEvent } = useTracking();

  return (
    <>
      <If match={showRegoModal}>
        {() => (
          <GuestRegistrationModal
            canClose={true}
            removeWindowScroll={true}
            onLoginSuccess={() => {
              setShowRegoModal(false);
            }}
            onRegisterSuccess={() => {
              setShowRegoModal(false);
            }}
            onClose={() => {
              setShowRegoModal(false);
              trackEvent({
                type: 'track',
                subject: 'guest registration modal',
                modifier: 'dismissed',
                action: 'click',
              });
            }}
          />
        )}
      </If>
      <Grid
        gap="8px"
        templateColumns="max-content"
        autoFlow="column"
        alignItems="center"
      >
        <StyledToggleButton
          styleType="loud"
          state={isUpVoted ? 'on' : 'off'}
          onClick={() => {
            if (!loggedIn) {
              setShowRegoModal(true);
              return;
            }
            mutate({ narrativeId, voteType: 1 });
            trackEvent({ action: 'clicked', modifier: 'upVote' });
          }}
        >
          <ChevronUpSmallSolid color="white" opacity={1} />
        </StyledToggleButton>
        <Text color="white" typography="xSmall" as="span">
          {upVotes - downVotes}
        </Text>
        <StyledToggleButton
          styleType="loud"
          state={isDownVoted ? 'on' : 'off'}
          onClick={() => {
            if (!loggedIn) {
              setShowRegoModal(true);
              return;
            }
            mutate({ narrativeId, voteType: -1 });
            trackEvent({ action: 'clicked', modifier: 'downVote' });
          }}
        >
          <ChevronDownSmallSolid color="white" />
        </StyledToggleButton>
      </Grid>
    </>
  );
};

export default NarrativeVoting;
